import type { FC, Ref } from "react";

import { InteractionModeChip } from "components/materials/page/interaction-mode/InteractionModeChip";
import { InteractionModeListbox } from "components/materials/page/interaction-mode/InteractionModeListbox";
import { InteractionMode, PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { DualButton } from "mds/components/DualButton";
import { Tooltip } from "mds/components/Tooltip";
import { useCapabilities } from "store/capability-hooks";
import { isGroupPage } from "utils/page-utils";

export type PageOverviewInteractionModeProps = {
  page: PageType;
  pageGroupIndex: number;
  hasSubmissions: boolean;
  canChangeMode: boolean;
  onModeChange?: (mode: InteractionMode) => void;
  onGroupCardToggle?: () => void;
  ref?: Ref<HTMLButtonElement>;
};

export const PageOverviewInteractionMode: FC<PageOverviewInteractionModeProps> = ({
  page,
  pageGroupIndex,
  hasSubmissions,
  canChangeMode,
  onModeChange,
  onGroupCardToggle,
  ref,
}) => {
  const { canParticipateInSection } = useCapabilities();
  const modeLocked = !canChangeMode || hasSubmissions;
  const submissionsTooltip =
    hasSubmissions && canChangeMode ? t("tooltip.group_config_has_submissions") : "";

  if (page.interaction_mode === InteractionMode.STATIC) {
    return null;
  }

  if (canParticipateInSection) {
    return (
      <InteractionModeChip
        page={page}
        pageGroupIndex={pageGroupIndex}
        ref={ref}
        onGroupCardToggle={onGroupCardToggle}
      />
    );
  }

  if (modeLocked) {
    return (
      <Tooltip content={submissionsTooltip} positionStrategy="fixed">
        <InteractionModeChip
          hasTooltip={!!submissionsTooltip}
          page={page}
          pageGroupIndex={pageGroupIndex}
          ref={ref}
          onGroupCardToggle={isGroupPage(page) ? onGroupCardToggle : undefined}
        />
      </Tooltip>
    );
  }

  if (!onModeChange) {
    return null;
  }

  if (isGroupPage(page)) {
    return (
      <DualButton
        buttonLeft={
          <InteractionModeChip
            page={page}
            pageGroupIndex={pageGroupIndex}
            ref={ref}
            onGroupCardToggle={onGroupCardToggle}
          />
        }
        buttonRight={<InteractionModeListbox page={page} chevronOnly onModeChange={onModeChange} />}
      />
    );
  }

  return <InteractionModeListbox page={page} ref={ref} onModeChange={onModeChange} />;
};

PageOverviewInteractionMode.displayName = "PageOverviewInteractionMode";
